import React, { useCallback, useEffect, useRef } from 'react';
import scriptjs from 'scriptjs';
import { GReCAPTCHA } from '@packages/reCaptcha/types';

import './styles/index.module.scss';

declare global {
	interface Window {
		grecaptcha: GReCAPTCHA;
	}
}

type ReCaptchaProps = {
	sitekey: string;
	onVerifySuccess: (token: string) => void;
	children: (captcha: { execute: () => void }) => React.ReactNode;
};

const API_URL = 'https://www.google.com/recaptcha/api.js';

const ReCaptcha: React.FC<ReCaptchaProps> = ({ sitekey, children, onVerifySuccess }) => {
	const widgetRef = useRef<HTMLDivElement>(null);
	const captchaRef = useRef<number>(0);
	const onVerifySuccessRef = useRef(onVerifySuccess);

	const execute = useCallback(() => {
		window.grecaptcha.reset(captchaRef.current);

		const token = window.grecaptcha.getResponse(captchaRef.current);
		if (token) {
			onVerifySuccessRef.current(token);
		} else {
			window.grecaptcha.execute(captchaRef.current);
		}
	}, []);

	useEffect(() => {
		scriptjs(API_URL, () => {
			window.grecaptcha.ready(() => {
				if (widgetRef.current) {
					captchaRef.current = window.grecaptcha.render(widgetRef.current, {
						sitekey,
						size: 'invisible',
						callback: onVerifySuccessRef.current,
					});
				}
			});
		});
	}, [sitekey]);

	// It's necessary for ModalWindow when recaptcha does not know about scroll position and make side effects
	// for ReCaptchaChallengeDialogWindow
	useEffect(() => {
		let recaptchaFrame: HTMLIFrameElement | null = null;

		const interval = setInterval(() => {
			const htmlTop = document.querySelector('html')!.style.top;

			recaptchaFrame = document.querySelector<HTMLIFrameElement>(
				'iframe[src*="google.com/recaptcha/api2/bframe"]',
			);

			if (recaptchaFrame !== null && recaptchaFrame?.parentNode?.parentNode) {
				const parentWrap = recaptchaFrame.parentNode!.parentNode! as HTMLDivElement;
				const parentTop = parseFloat(parentWrap.style.top);
				const scrollTop = -1 * parseFloat(htmlTop);

				if (htmlTop && parentTop > 0 && parentTop < scrollTop) {
					parentWrap.style.top = `${scrollTop}px`;
				}
			}
		}, 1000);

		return () => {
			clearInterval(interval);

			if (recaptchaFrame !== null && recaptchaFrame?.parentNode?.parentNode) {
				const parentWrap = recaptchaFrame.parentNode!.parentNode! as HTMLDivElement;
				parentWrap.remove();
			}
		};
	}, []);

	return (
		<>
			{children({ execute })}
			<div
				ref={widgetRef}
				className={'recaptcha'}
			/>
		</>
	);
};

export { ReCaptcha };
